import { SERP_FEATURES_MAP } from "app/common/serp/SerpFilterMaps";
import type { ESerpTypes } from "app/common/serp/SerpFilterMaps";

import { DEFAULT_EXCLUDED_FEATURES, SUPPORTED_EXCLUDED_FEATURES } from "./constants";
import type { ExcludedFeatureOption } from "./types";

const SUPPORTED_EXCLUDED_FEATURES_SET = new Set<string>(SUPPORTED_EXCLUDED_FEATURES);

const isSupportedExcludedFeature = (value: string): value is ESerpTypes => SUPPORTED_EXCLUDED_FEATURES_SET.has(value);

export const sanitizeExcludedFeatures = (raw: readonly string[] | undefined | null): ESerpTypes[] => {
    if (!raw?.length) {
        return [];
    }

    const sanitized: ESerpTypes[] = [];
    for (const value of raw) {
        if (isSupportedExcludedFeature(value) && !sanitized.includes(value)) {
            sanitized.push(value);
        }
    }
    return sanitized;
};

/** Omitted/null → default Ads; explicit array (incl. empty) → sanitized list. */
export const getInitialExcludedFeatures = (raw: readonly string[] | undefined | null): ESerpTypes[] => {
    if (raw === undefined || raw === null) {
        return [...DEFAULT_EXCLUDED_FEATURES];
    }
    return sanitizeExcludedFeatures(raw);
};

export const isAllExcluded = (excluded: readonly ESerpTypes[]): boolean => {
    if (excluded.length < SUPPORTED_EXCLUDED_FEATURES.length) {
        return false;
    }
    const excludedSet = new Set(excluded);
    return SUPPORTED_EXCLUDED_FEATURES.every((feature) => excludedSet.has(feature));
};

export const areExcludedFeaturesEqual = (
    left: readonly ESerpTypes[] | undefined,
    right: readonly ESerpTypes[] | undefined,
): boolean => {
    const leftValues = left ?? DEFAULT_EXCLUDED_FEATURES;
    const rightValues = right ?? DEFAULT_EXCLUDED_FEATURES;

    if (leftValues.length !== rightValues.length) {
        return false;
    }

    const rightSet = new Set(rightValues);
    return leftValues.every((value) => rightSet.has(value));
};

export const buildExcludedFeatureOptions = (): ExcludedFeatureOption[] =>
    SUPPORTED_EXCLUDED_FEATURES.map((id) => ({
        id,
        text: SERP_FEATURES_MAP[id].name,
    })).sort((a, b) => a.text.localeCompare(b.text));

export const excludedFeaturesToOptions = (excluded: readonly ESerpTypes[]): ExcludedFeatureOption[] => {
    const excludedSet = new Set(excluded);
    return buildExcludedFeatureOptions().filter((option) => excludedSet.has(option.id));
};

export const optionsToExcludedFeatures = (options: readonly ExcludedFeatureOption[]): ESerpTypes[] =>
    options.map((option) => option.id);
